"use client";

import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader, IScannerControls } from "@zxing/browser";
import {
  BarcodeFormat,
  DecodeHintType,
  NotFoundException,
} from "@zxing/library";
import { decode as decodeBcbp } from "bcbp";
import type { BoardingPassData } from "bcbp";
import { Camera, ScanLine, MapPin, X } from "lucide-react";

const NAVY = "#102542";
const YELLOW = "#ffb800";

// Los boarding pass impresos usan PDF417; los móviles Aztec o QR.
const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.PDF_417,
  BarcodeFormat.AZTEC,
  BarcodeFormat.QR_CODE,
  BarcodeFormat.DATA_MATRIX,
]);
HINTS.set(DecodeHintType.TRY_HARDER, true);

const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
};

interface BoardingScannerProps {
  onScanned: (data: BoardingPassData) => void;
}

export default function BoardingScanner({ onScanned }: BoardingScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);

  const [showPermissions, setShowPermissions] = useState(false);
  const [showScanModal, setShowScanModal] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    return () => controlsRef.current?.stop();
  }, []);

  // Bloquea el scroll del fondo mientras un modal está abierto.
  useEffect(() => {
    const open = showPermissions || showScanModal;
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [showPermissions, showScanModal]);

  // Arranca la cámara una vez que el modal (y el <video>) están montados.
  useEffect(() => {
    if (showScanModal) {
      startScan();
    } else {
      stopCamera();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showScanModal]);

  function stopCamera() {
    controlsRef.current?.stop();
    controlsRef.current = null;
  }

  function closeScanModal() {
    setShowScanModal(false);
    setSuccess(false);
  }

  // Pre-solicita la ubicación para que el mapa pueda situar a la persona.
  function requestLocation() {
    if (typeof navigator === "undefined" || !navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      () => {},
      () => {},
      { timeout: 8000, maximumAge: 60_000 },
    );
  }

  // El usuario aceptó el tutorial: pide ubicación y abre la cámara.
  function acceptAndScan() {
    setError("");
    requestLocation();
    setShowPermissions(false);
    setShowScanModal(true);
  }

  async function startScan() {
    setError("");
    setSuccess(false);

    try {
      const reader = new BrowserMultiFormatReader(HINTS);
      controlsRef.current = await reader.decodeFromConstraints(
        VIDEO_CONSTRAINTS,
        videoRef.current!,
        (result, err) => {
          if (result) {
            stopCamera();
            handleText(result.getText());
          }
          if (err && !(err instanceof NotFoundException)) {
            stopCamera();
            setError(`${err.name}: ${err.message}`);
          }
        },
      );
    } catch (e) {
      const err = e as Error;
      setError(`${err.name}: ${err.message}`);
    }
  }

  function handleText(text: string) {
    try {
      const bcbp = decodeBcbp(text);
      if (!bcbp.data) {
        throw new Error("El código no contiene datos de boarding pass.");
      }
      setSuccess(true);
      setShowScanModal(false);
      onScanned(bcbp.data);
    } catch (e) {
      const err = e as Error;
      setError(
        `Se leyó el código pero no es un boarding pass válido (BCBP). ${err.message}`,
      );
    }
  }

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wider font-semibold transition-opacity hover:opacity-90";
  const ghostBtn =
    "inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm uppercase tracking-wider font-semibold border transition-colors";

  return (
    <div>
      {/* Disparador principal */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => {
            setError("");
            setShowPermissions(true);
          }}
          className={primaryBtn}
          style={{ backgroundColor: YELLOW, color: NAVY }}
        >
          <ScanLine className="w-4 h-4" />
          Escanear boarding pass
        </button>
      </div>

      {/* Error fuera del modal (p. ej. boarding pass inválido tras cerrar la cámara) */}
      {error && !showScanModal && (
        <div
          className="mt-5 p-4 rounded-xl text-sm leading-relaxed max-w-md"
          style={{
            backgroundColor: "#fef2f2",
            color: "#991b1b",
            borderLeft: "3px solid #dc2626",
          }}
        >
          <strong className="block mb-1 uppercase tracking-wider text-xs font-mono">
            Error al leer
          </strong>
          {error}
        </div>
      )}

      {/* Modal tutorial de permisos */}
      {showPermissions && (
        <div
          className="fixed inset-0 z-60 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="permisos-titulo"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowPermissions(false)}
          />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
            <h3
              id="permisos-titulo"
              className="text-xl font-bold tracking-tight mb-1"
              style={{ color: NAVY }}
            >
              Antes de empezar
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Para guiarte a tu puerta necesitamos dos permisos:
            </p>

            <div className="flex gap-4 mb-4">
              <div
                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${YELLOW}1f`, color: NAVY }}
              >
                <Camera className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Cámara</p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Para escanear el código de tu boarding pass.
                </p>
              </div>
            </div>

            <div className="flex gap-4 mb-7">
              <div
                className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${YELLOW}1f`, color: NAVY }}
              >
                <MapPin className="w-5 h-5" />
              </div>
              <div>
                <p className="font-semibold text-gray-900">Ubicación</p>
                <p className="text-sm text-gray-500 leading-relaxed">
                  Para mostrarte en el mapa la ruta hacia tu puerta.
                </p>
              </div>
            </div>

            <button
              onClick={acceptAndScan}
              className={`${primaryBtn} w-full`}
              style={{ backgroundColor: YELLOW, color: NAVY }}
            >
              Permitir y empezar
            </button>
            <button
              onClick={() => setShowPermissions(false)}
              className="w-full mt-2 py-3 text-sm font-semibold text-gray-500 hover:text-gray-700 transition-colors"
            >
              Ahora no
            </button>
          </div>
        </div>
      )}

      {/* Modal de escaneo a pantalla completa */}
      {showScanModal && (
        <div
          className="fixed inset-0 z-60 flex flex-col"
          style={{ backgroundColor: NAVY }}
          role="dialog"
          aria-modal="true"
          aria-label="Escanear boarding pass"
        >
          {/* Barra superior */}
          <div className="flex items-center justify-between px-4 py-4 text-white">
            <span className="text-sm uppercase tracking-[0.2em] font-semibold">
              Escanear
            </span>
            <button
              onClick={closeScanModal}
              aria-label="Cerrar"
              className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Cámara */}
          <div className="flex-1 relative flex items-center justify-center overflow-hidden">
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-cover"
              style={{ background: "#000" }}
              playsInline
              muted
            />
            <div
              className="relative rounded-xl"
              style={{
                width: "85%",
                maxWidth: 420,
                aspectRatio: "2 / 1",
                border: `4px solid ${success ? "#22c55e" : YELLOW}`,
                boxShadow: "0 0 0 9999px rgba(16,37,66,0.45)",
                pointerEvents: "none",
              }}
            />
          </div>

          {/* Pie con instrucción y error */}
          <div className="px-5 pb-8 pt-5 text-center">
            {error ? (
              <div
                className="mx-auto max-w-md p-4 rounded-xl text-sm leading-relaxed text-left mb-4"
                style={{
                  backgroundColor: "#fef2f2",
                  color: "#991b1b",
                  borderLeft: "3px solid #dc2626",
                }}
              >
                <strong className="block mb-1 uppercase tracking-wider text-xs font-mono">
                  Error al leer
                </strong>
                {error}
              </div>
            ) : (
              <p className="text-white/80 text-sm flex items-center justify-center gap-2 mb-5">
                <ScanLine className="w-4 h-4" style={{ color: YELLOW }} />
                Apunta al código de barras{" "}
                <strong className="text-white">horizontal</strong> dentro del
                recuadro.
              </p>
            )}

            {error ? (
              <button
                onClick={() => {
                  setError("");
                  startScan();
                }}
                className={`${primaryBtn} w-full max-w-xs`}
                style={{ backgroundColor: YELLOW, color: NAVY }}
              >
                Reintentar
              </button>
            ) : (
              <button
                onClick={closeScanModal}
                className={`${ghostBtn} w-full max-w-xs border-white/30 text-white`}
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
